/**
 * composeGreeting
 *
 * Pure client-side composer for the personalized new-thread greeting.
 * Every NEW chat thread on threading surfaces opens with a CONCISE assistant
 * greeting personalized to the account. Pure composition, ZERO network —
 * everything is derived from data the client has in hand at + time.
 *
 * REVISED CONTRACT (owner directive, Miles 2026-07-13 night):
 * Threads are per project/task — the greeting must NEVER invite the user to
 * pull a previous task into this new thread. The old "Pick up where you left
 * off: <thread list>" shape is BANNED. Instead the greeting recommends
 * NET-NEW automations grounded in the account's memory system:
 *
 *  • `suggestedTasks` — short memory-distilled suggestion strings, produced
 *    server-side (refreshGreetingSuggestions reads accounts/{id}/memory and
 *    writes config runtime docs the client already loads). The composer
 *    renders up to 3, FILTERED against the currently-open thread span so
 *    every rendered suggestion is net-new relative to what's in flight.
 *  • `openThreads` — the live thread records. Used ONLY as the exclusion
 *    set for that filter; titles are NEVER rendered into the greeting.
 *  • Suggestions present → hello + "a few things I can take on" list +
 *    "or tell me what you need" closing. Total text under ~500 chars.
 *  • No (surviving) suggestions but a configured welcome → EXACTLY the
 *    current welcome copy (text verbatim; the caller may substitute its own
 *    richer html for this case — see GlobalChatSystem's composer callback).
 *  • No welcome AND no suggestions → null (caller falls back to legacy
 *    behavior, i.e. an empty thread).
 *
 * Deliberately imports NOTHING from useChatThreads so this module stays a
 * leaf (usable from any surface without dragging the thread store in).
 */

export interface GreetingRecentThread {
  /** Thread chip title (derived, labeler-produced, or user-set). */
  title: string;
  /** Optional 1–2 sentence topic summary from the thread labeler. */
  topicSummary?: string;
  /** Persisted message count on the thread record. */
  messageCount: number;
  /** True when the user explicitly renamed the thread. */
  userRenamed?: boolean;
}

export interface ComposePersonalizedGreetingParams {
  /** The surface's configured static welcome text (config.welcomeMessage.text). */
  baseWelcome: string | null;
  /** Optional assistant persona name (e.g. "Prest0n"). */
  personaName?: string;
  /** Optional firm/tenant name. */
  firmName?: string | null;
  /**
   * The account's currently-open thread records (exclusion span). NEVER
   * rendered — a suggestion that overlaps one of these is dropped so the
   * greeting only ever proposes net-new work.
   */
  openThreads: Array<GreetingRecentThread>;
  /**
   * Memory-distilled net-new automation suggestions (server-produced,
   * account-specific). Rendered up to 3 after the open-thread filter.
   */
  suggestedTasks?: string[];
  /** BCP-47-ish UI language tag; anything starting with "es" → Spanish. */
  uiLanguage: string;
}

/** Max suggestion items in the greeting. */
const MAX_ITEMS = 3;
/** Soft ceiling for the composed text (spec: "under ~500 chars"). */
const TEXT_BUDGET = 500;
/** Hard cap per rendered suggestion. */
const SUGGESTION_MAX_LEN = 120;

/** Minimal HTML-entity escape for user/model-derived strings. */
function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Normalize a phrase into comparison tokens: lowercase, accents stripped,
 * punctuation dropped, tokens shorter than 4 chars ignored (articles,
 * pronouns, "the"/"para"-class noise).
 */
export function significantTokens(raw: string): Set<string> {
  const normalized = (raw || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ');
  const tokens = normalized.split(/\s+/).filter((t) => t.length >= 4);
  return new Set(tokens);
}

/**
 * NET-NEW filter: a suggestion overlaps an open thread when they share ≥2
 * significant tokens, or the smaller token set is fully contained in the
 * larger (single-topic titles like "Visa research" vs "research visa
 * timelines"). Conservative on purpose — dropping a good suggestion is
 * cheap; recommending the user restart an in-flight project inside a fresh
 * thread is exactly the cross-threading the owner banned.
 */
export function suggestionOverlapsThread(suggestion: string, thread: GreetingRecentThread): boolean {
  const s = significantTokens(suggestion);
  const combined = `${thread.title || ''} ${thread.topicSummary || ''}`;
  const t = significantTokens(combined);
  if (s.size === 0 || t.size === 0) return false;
  let shared = 0;
  const smaller = s.size <= t.size ? s : t;
  const larger = s.size <= t.size ? t : s;
  for (const token of smaller) {
    if (larger.has(token)) shared += 1;
  }
  if (shared >= 2) return true;
  return shared === smaller.size && smaller.size >= 1 && smaller.size <= 2;
}

function buildCopy(lang: 'en' | 'es', personaName?: string, firmName?: string | null): {
  hello: string;
  leadIn: string;
  closing: string;
} {
  const persona = (personaName || '').trim();
  const firm = (firmName || '').trim();
  if (lang === 'es') {
    const hello = persona && firm
      ? `¡Hola! Soy ${persona}, de ${firm} — qué gusto verte de nuevo.`
      : persona
        ? `¡Hola! Soy ${persona} — qué gusto verte de nuevo.`
        : firm
          ? `¡Hola! Qué gusto tenerte de nuevo en ${firm}.`
          : '¡Hola! Qué gusto verte de nuevo.';
    return {
      hello,
      leadIn: 'Algunas cosas nuevas que puedo hacer por ti:',
      closing: 'O dime qué necesitas.',
    };
  }
  const hello = persona && firm
    ? `Hi! ${persona} from ${firm} here — good to see you again.`
    : persona
      ? `Hi! ${persona} here — good to see you again.`
      : firm
        ? `Hi! Welcome back to ${firm}.`
        : 'Hi! Good to see you again.';
  return {
    hello,
    leadIn: 'A few new things I can take on for you:',
    closing: 'Or tell me what you need.',
  };
}

function renderText(copy: { hello: string; leadIn: string; closing: string }, items: string[]): string {
  const lines = items.map((item) => `• ${item}`);
  return `${copy.hello}\n\n${copy.leadIn}\n${lines.join('\n')}\n\n${copy.closing}`;
}

function renderHtml(copy: { hello: string; leadIn: string; closing: string }, items: string[]): string {
  const lis = items.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  return `<p>${escapeHtml(copy.hello)}</p><p>${escapeHtml(copy.leadIn)}</p><ul>${lis}</ul><p>${escapeHtml(copy.closing)}</p>`;
}

/**
 * Compose the personalized new-thread greeting. Pure and synchronous.
 * Returns null when there is nothing to say (no configured welcome AND no
 * surviving suggestions) — the caller falls back to legacy behavior.
 *
 * `usedSuggestions` tells the caller whether memory-distilled suggestion
 * items were actually rendered (true) or the copy is the verbatim
 * baseWelcome fallback (false). The greeting's ⓘ popup attributes the
 * distillation compute only when its output is really on screen.
 */
export function composePersonalizedGreeting(params: ComposePersonalizedGreetingParams): {
  text: string;
  html: string;
  usedSuggestions: boolean;
} | null {
  const baseWelcome = typeof params.baseWelcome === 'string' && params.baseWelcome.trim().length > 0
    ? params.baseWelcome
    : null;

  const openThreads = params.openThreads || [];
  const items = (params.suggestedTasks || [])
    .map((raw) => (raw || '').replace(/\s+/g, ' ').trim())
    .filter((s) => s.length > 0)
    .map((s) => (s.length > SUGGESTION_MAX_LEN ? `${s.slice(0, SUGGESTION_MAX_LEN - 1).replace(/[\s,;:.]+$/, '')}…` : s))
    // NET-NEW: drop anything that overlaps an open thread's title/topic.
    .filter((s) => !openThreads.some((thread) => suggestionOverlapsThread(s, thread)))
    .slice(0, MAX_ITEMS);

  if (items.length === 0) {
    // No memory-grounded net-new suggestions to offer: the configured
    // welcome verbatim (accounts get their onboarding-personalized copy),
    // else nothing. NEVER a thread list.
    if (baseWelcome === null) return null;
    return { text: baseWelcome, html: `<p>${escapeHtml(baseWelcome)}</p>`, usedSuggestions: false };
  }

  const lang: 'en' | 'es' = /^es/i.test((params.uiLanguage || '').trim()) ? 'es' : 'en';
  const copy = buildCopy(lang, params.personaName, params.firmName);

  // Conciseness enforcement: stay under the ~500-char budget by dropping
  // trailing suggestions (each is already ≤120 chars).
  let rendered = items;
  let text = renderText(copy, rendered);
  while (text.length > TEXT_BUDGET && rendered.length > 1) {
    rendered = rendered.slice(0, rendered.length - 1);
    text = renderText(copy, rendered);
  }

  return { text, html: renderHtml(copy, rendered), usedSuggestions: true };
}

export default composePersonalizedGreeting;
